import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line,
  RadialBarChart, RadialBar, XAxis, YAxis, Tooltip,
} from "recharts";
import { Activity, Cpu, Server, ShieldCheck, Wifi, Bell } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { LiveTerminal } from "@/components/site/LiveTerminal";
import { useI18n } from "@/i18n";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const seed = (n, base, amp) =>
  Array.from({ length: n }, (_, i) => ({
    t: i,
    a: clamp(base + Math.sin(i / 2) * amp + (Math.random() - 0.5) * amp, 2, 98),
    b: clamp(base * 0.7 + Math.cos(i / 3) * amp + (Math.random() - 0.5) * amp, 2, 98),
  }));

const Panel = ({ title, icon: Icon, accent = "#00E5FF", children, right }) => (
  <div className="card-edge relative overflow-hidden rounded-xl p-5">
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <span
          className="grid h-8 w-8 place-items-center rounded-md border border-white/10"
          style={{ color: accent }}
        >
          <Icon size={16} />
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">
          {title}
        </span>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const LiveDot = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1">
    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]" />
    <span className="font-mono text-[9px] uppercase tracking-widest text-emerald-300">live</span>
  </span>
);

const ChartTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/10 bg-ink-900/95 px-3 py-2 backdrop-blur-md">
      {payload.map((p) => (
        <div key={p.dataKey} className="font-mono text-[11px]" style={{ color: p.color }}>
          {Math.round(p.value)}
        </div>
      ))}
    </div>
  );
};

const KPI = ({ label, value, unit, trend, color }) => (
  <div className="card-edge rounded-xl p-4">
    <div className="font-mono text-[10px] uppercase tracking-widest text-white/45">{label}</div>
    <div className="mt-2 flex items-end gap-1">
      <span className="font-display text-3xl font-extrabold" style={{ color }}>{value}</span>
      <span className="mb-1 font-mono text-xs text-white/40">{unit}</span>
    </div>
    <div className="mt-1 font-mono text-[10px] text-white/40">{trend}</div>
  </div>
);

const VPN_TUNNELS = [
  { name: "HQ ↔ Cloud", loc: "Bucureşti → Frankfurt" },
  { name: "Sediu 2 ↔ HQ", loc: "Cluj → Bucureşti" },
  { name: "Backup Site", loc: "PBS → Offsite" },
  { name: "Remote Admin", loc: "WireGuard" },
];

export const LiveMonitor = () => {
  const { t } = useI18n();
  const [cpu, setCpu] = useState(() => seed(28, 42, 22));
  const [net, setNet] = useState(() => seed(28, 55, 26));
  const [ram, setRam] = useState(63);
  const [alerts, setAlerts] = useState(2);
  const [latency, setLatency] = useState(14);
  const [tick, setTick] = useState(0);
  const cardRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      setCpu((p) => {
        const last = p[p.length - 1];
        const next = {
          t: last.t + 1,
          a: clamp(last.a + (Math.random() - 0.5) * 18, 8, 95),
          b: clamp(last.b + (Math.random() - 0.5) * 14, 5, 85),
        };
        return [...p.slice(1), next];
      });
      setNet((p) => {
        const last = p[p.length - 1];
        const next = {
          t: last.t + 1,
          a: clamp(last.a + (Math.random() - 0.5) * 22, 10, 98),
          b: clamp(last.b + (Math.random() - 0.5) * 16, 6, 80),
        };
        return [...p.slice(1), next];
      });
      setRam((r) => Math.round(clamp(r + (Math.random() - 0.5) * 8, 45, 82)));
      setLatency((l) => Math.round(clamp(l + (Math.random() - 0.5) * 6, 6, 34)));
      setAlerts((a) => clamp(a + (Math.random() > 0.8 ? 1 : Math.random() > 0.7 ? -1 : 0), 0, 6));
      setTick((t) => t + 1);
    }, 1600);
    return () => clearInterval(id);
  }, []);

  const ramData = useMemo(
    () => [{ name: "ram", value: ram, fill: "#00E5FF" }],
    [ram]
  );
  const uptime = (99.94 + Math.sin(tick / 20) * 0.04).toFixed(2);

  return (
    <section
      id="monitorizare"
      data-testid="monitor-section"
      className="relative overflow-hidden border-t border-white/10 bg-ink-800"
    >
      <div className="pointer-events-none absolute -right-40 top-0 h-[36rem] w-[36rem] rounded-full bg-cyan-neon/8 blur-[150px]" />
      <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <Reveal>
              <SectionLabel index="[ • ]">{t("mon.label")}</SectionLabel>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                {t("mon.h1")}{" "}
                <span className="text-aurora">{t("mon.hkw")}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55">
                {t("mon.sub")}
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <LiveDot />
          </Reveal>
        </div>

        <motion.div
          ref={cardRef}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12"
        >
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KPI label={t("mon.kpi.uptime")} value={uptime} unit="%" trend={t("mon.kpi.sla")} color="#34d399" />
            <KPI label={t("mon.kpi.servers")} value="18" unit="/ 18" trend={t("mon.kpi.offline")} color="#00E5FF" />
            <KPI label={t("mon.kpi.latency")} value={latency} unit="ms" trend={t("mon.kpi.intersite")} color="#7C9CFC" />
            <KPI label={t("mon.kpi.alerts")} value={alerts} unit="" trend={alerts === 0 ? t("mon.kpi.ok") : t("mon.kpi.handling")} color={alerts > 3 ? "#f59e0b" : "#00E5FF"} />
          </div>

          {/* Charts */}
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Panel title={t("mon.p.cpu")} icon={Cpu} right={<LiveDot />}>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cpu} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                    <XAxis dataKey="t" hide />
                    <YAxis domain={[0, 100]} tick={{ fill: "#6b7688", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTip />} cursor={{ stroke: "#ffffff20" }} />
                    <Line type="monotone" dataKey="a" stroke="#00E5FF" strokeWidth={2} dot={false} isAnimationActive={false} />
                    <Line type="monotone" dataKey="b" stroke="#7C5CFC" strokeWidth={2} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel title={t("mon.p.net")} icon={Activity} accent="#34d399" right={<LiveDot />}>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={net} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="t" hide />
                    <YAxis domain={[0, 100]} tick={{ fill: "#6b7688", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTip />} cursor={{ stroke: "#ffffff20" }} />
                    <Area type="monotone" dataKey="a" stroke="#00E5FF" strokeWidth={2} fill="url(#gIn)" isAnimationActive={false} />
                    <Area type="monotone" dataKey="b" stroke="#34d399" strokeWidth={2} fill="url(#gOut)" isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel title={t("mon.p.ram")} icon={Server} accent="#7C5CFC">
              <div className="relative h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart
                    innerRadius="70%" outerRadius="100%" data={ramData}
                    startAngle={220} endAngle={-40}
                  >
                    <RadialBar background={{ fill: "#1a2433" }} dataKey="value" cornerRadius={20} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-display text-4xl font-extrabold text-white">{ram}%</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("mon.ram.total")}</span>
                </div>
              </div>
            </Panel>
          </div>

          {/* VPN + alerts */}
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Panel title={t("mon.p.vpn")} icon={Wifi} accent="#34d399">
              <div className="flex flex-col divide-y divide-white/5">
                {VPN_TUNNELS.map((v, i) => (
                  <div key={v.name} className="flex items-center justify-between py-2.5">
                    <div>
                      <div className="font-display text-sm font-semibold text-white">{v.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">{v.loc}</div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase text-emerald-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]" style={{ animation: `pulse 2s ${i * 0.3}s infinite` }} />
                      up
                    </span>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title={t("mon.p.fw")} icon={ShieldCheck} accent="#00E5FF">
              <div className="grid h-full grid-cols-2 gap-3">
                {[
                  { k: t("mon.fw.blocks"), v: (1240 + tick * 3).toLocaleString("ro-RO") },
                  { k: t("mon.fw.rules"), v: "342" },
                  { k: "Multi-WAN", v: "OK" },
                  { k: "TLS / SSL", v: "A+" },
                ].map((s) => (
                  <div key={s.k} className="rounded-lg border border-white/8 bg-ink-900/60 p-3">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">{s.k}</div>
                    <div className="mt-1 font-display text-lg font-bold text-cyan-neon">{s.v}</div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title={t("mon.p.alerts")} icon={Bell} accent="#f59e0b">
              <div className="flex flex-col gap-2.5 font-mono text-[11px]">
                {[
                  { c: "#34d399", t: "OK", m: t("mon.al.1") },
                  { c: "#00E5FF", t: "INFO", m: t("mon.al.2") },
                  { c: "#f59e0b", t: "WARN", m: t("mon.al.3") },
                  { c: "#34d399", t: "OK", m: t("mon.al.4") },
                ].map((a, i) => (
                  <div key={i} className="flex items-center gap-2 border-l-2 pl-2.5" style={{ borderColor: a.c }}>
                    <span className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold" style={{ color: a.c, background: `${a.c}1a` }}>{a.t}</span>
                    <span className="truncate text-white/60">{a.m}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          <div className="mt-4">
            <LiveTerminal />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
